import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Select, 
  DatePicker, 
  Button, 
  Card, 
  Space, 
  Tag, 
  message, 
  Typography,
  Row,
  Col,
  Input,
  Empty,
  Statistic
} from 'antd';
import { SearchOutlined, ReloadOutlined, FilterOutlined, BarChartOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '@/services/api';
import { WorkRecord, Project, User, RecordType, RecordStatus } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface AllLogsPageProps {}

const AllLogs: React.FC<AllLogsPageProps> = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<WorkRecord[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | undefined>(undefined);
  const [selectedUser, setSelectedUser] = useState<number | undefined>(undefined);
  const [selectedRecordType, setSelectedRecordType] = useState<RecordType | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 统计数据
  const [stats, setStats] = useState({
    totalRecords: 0,
    totalHours: 0,
    totalCost: 0,
    activeUsers: 0
  });

  // 记录类型映射
  const recordTypeMap: Record<RecordType, { label: string; color: string }> = {
    'check_in': { label: '签到', color: 'blue' },
    'check_out': { label: '签退', color: 'green' },
    'work': { label: '工作', color: 'orange' },
    'leave': { label: '请假', color: 'red' },
    'business_trip': { label: '出差', color: 'purple' }
  };

  // 状态映射
  const statusMap: Record<string, { label: string; color: string }> = {
    'active': { label: '活跃', color: 'green' },
    'archived': { label: '已归档', color: 'gray' },
    'pending': { label: '待审批', color: 'orange' },
    'approved': { label: '已批准', color: 'green' },
    'rejected': { label: '已拒绝', color: 'red' }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadAllRecords();
  }, [selectedProject, selectedUser, selectedRecordType, dateRange, pagination.current, pagination.pageSize]);

  const loadInitialData = async () => {
    try {
      // 加载所有项目
      const projectsData = await api.getProjects();
      setProjects(projectsData);

      // 加载所有用户（这里可能需要新的API接口）
      // 暂时从记录中提取用户信息
    } catch (error) {
      console.error('加载初始数据失败:', error);
      message.error('加载数据失败');
    }
  };

  const loadAllRecords = async () => {
    setLoading(true);
    try {
      const params: any = {
        skip: ((pagination.current || 1) - 1) * (pagination.pageSize || 10),
        limit: pagination.pageSize || 10,
      };

      if (selectedProject) params.project_id = selectedProject;
      if (selectedUser) params.user_id = selectedUser;
      if (selectedRecordType) params.record_type = selectedRecordType;
      if (dateRange) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      const data = await api.getAllWorkRecords(params);
      setRecords(data);

      // 从记录中提取用户信息
      const uniqueUsers = Array.from(
        new Map(data.map(record => [record.user_id, record.user]).filter(([_, user]) => user))
          .values()
      ) as User[];
      setUsers(uniqueUsers);

      // 计算统计数据
      const totalHours = data.reduce((sum, record) => sum + (record.duration_hours || 0), 0);
      const totalCost = data.reduce((sum, record) => sum + (record.calculated_cost || 0), 0);
      const activeUsers = new Set(data.map(record => record.user_id)).size;

      setStats({
        totalRecords: data.length,
        totalHours,
        totalCost,
        activeUsers
      });
      
    } catch (error: any) {
      console.error('加载所有记录失败:', error);
      if (error.response?.status === 403) {
        message.error('您没有权限查看所有工作记录');
      } else {
        message.error('加载工作记录失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedProject(undefined);
    setSelectedUser(undefined);
    setSelectedRecordType(undefined);
    setDateRange(null);
    setPagination({ ...pagination, current: 1 });
  };

  const columns: ColumnsType<WorkRecord> = [
    {
      title: '',
      key: 'record',
      render: (record: WorkRecord) => (
        <div>
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 500
          }}>
            <span>{record.user?.real_name || record.user?.username || '-'}</span>
            <span>{record.duration_hours?.toFixed(1) || '-'}小时</span>
            <span>¥{record.calculated_cost?.toFixed(2) || '0.00'}</span>
            <span>{record.project?.name || '-'}</span>
          </div>
          <div style={{ 
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#f8f8f8',
            borderRadius: '4px',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: '#666'
          }}>
            {record.description || '无具体描述'}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>全局工作记录</Title>
      
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="总记录数" 
              value={stats.totalRecords} 
              prefix={<BarChartOutlined />} 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="总工时(小时)" 
              value={stats.totalHours} 
              precision={1}
              prefix={<BarChartOutlined />} 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="总成本" 
              value={stats.totalCost} 
              precision={2}
              prefix="¥" 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="活跃用户" 
              value={stats.activeUsers} 
              prefix={<BarChartOutlined />} 
            />
          </Card>
        </Col>
      </Row>
      
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Select
              placeholder="选择项目"
              style={{ width: '100%' }}
              value={selectedProject}
              onChange={setSelectedProject}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {projects.map(project => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="选择用户"
              style={{ width: '100%' }}
              value={selectedUser}
              onChange={setSelectedUser}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {users.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.real_name || user.username} ({user.role})
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="记录类型"
              style={{ width: '100%' }}
              value={selectedRecordType}
              onChange={setSelectedRecordType}
              allowClear
            >
              {Object.entries(recordTypeMap).map(([type, info]) => (
                <Option key={type} value={type}>
                  {info.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
            />
          </Col>
        </Row>
        <Row style={{ marginTop: '16px' }}>
          <Col>
            <Space>
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={loadAllRecords}
              >
                查询
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={handleReset}
              >
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          showHeader={false}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          onChange={(paginationInfo) => {
            setPagination({
              current: paginationInfo.current,
              pageSize: paginationInfo.pageSize,
              total: paginationInfo.total,
            });
          }}
          locale={{
            emptyText: (
              <Empty
                description="暂无工作记录"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default AllLogs;